// SpacetimeDB connection context for Waytide. Ported from South Seas with
// Waytide-specific token keys and the workspace scope (one connection per
// authenticated session, not one per world). Wraps the authenticated
// workspace tree in App.tsx so channels, presence, and pending turns can
// subscribe via the SDK instead of HTTP fetch-poll.

import {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import {
  SpacetimeDBProvider as SdkProvider,
  useSpacetimeDB,
} from "spacetimedb/react";
import { DbConnection } from "../../spacetimedb-bindings";

const SPACETIMEDB_HOST =
  (import.meta.env.VITE_SPACETIMEDB_HOST as string | undefined) ??
  "wss://maincloud.spacetimedb.com";
const SPACETIMEDB_DB =
  (import.meta.env.VITE_SPACETIMEDB_DB as string | undefined) ??
  "waytide-staging";

const STDB_TOKEN_KEY = "wt_stdb_token";

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const RECONNECT_MAX_ATTEMPTS = 10;

function getBackoffDelay(attempt: number): number {
  return Math.min(RECONNECT_BASE_MS * Math.pow(2, attempt), RECONNECT_MAX_MS);
}

const ReconnectingContext = createContext(false);
const ConnectedContext = createContext(false);

export function useReconnecting(): boolean {
  return useContext(ReconnectingContext);
}

export function useConnected(): boolean {
  return useContext(ConnectedContext);
}

export function SpacetimeDBProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [reconnectTick, setReconnectTick] = useState(0);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalDisconnectRef = useRef(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    intentionalDisconnectRef.current = false;
    return () => {
      intentionalDisconnectRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (intentionalDisconnectRef.current) return;
    if (reconnectTimerRef.current) return;
    if (reconnectAttemptRef.current >= RECONNECT_MAX_ATTEMPTS) {
      console.warn(
        `[waytide] SpacetimeDB reconnect failed after ${RECONNECT_MAX_ATTEMPTS} attempts`,
      );
      setReconnecting(false);
      return;
    }

    const attempt = reconnectAttemptRef.current;
    const delay = getBackoffDelay(attempt);
    console.info(
      `[waytide] SpacetimeDB reconnecting in ${(delay / 1000).toFixed(1)}s (attempt ${attempt + 1}/${RECONNECT_MAX_ATTEMPTS})`,
    );
    setReconnecting(true);

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      reconnectAttemptRef.current += 1;
      setReconnectTick((t) => t + 1);
    }, delay);
  }, []);

  const builder = useMemo(() => {
    const savedToken = localStorage.getItem(STDB_TOKEN_KEY) ?? undefined;
    console.info("[waytide] Connecting to SpacetimeDB...");

    return DbConnection.builder()
      .withUri(SPACETIMEDB_HOST)
      .withDatabaseName(SPACETIMEDB_DB)
      .withToken(savedToken)
      .onConnect((_conn, identity, token) => {
        localStorage.setItem(STDB_TOKEN_KEY, token);
        console.info(
          `[waytide] SpacetimeDB connected — identity ${identity.toHexString().slice(0, 12)}...`,
        );
        reconnectAttemptRef.current = 0;
        setReconnecting(false);
        setConnected(true);
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      })
      .onConnectError((_ctx, error) => {
        console.error(`[waytide] SpacetimeDB connect error: ${error.message}`);
        scheduleReconnect();
      })
      .onDisconnect((_ctx, error) => {
        setConnected(false);
        if (error) {
          console.warn(
            `[waytide] SpacetimeDB disconnected: ${error.message}`,
          );
          scheduleReconnect();
        } else {
          console.info("[waytide] SpacetimeDB disconnected (clean)");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectTick, scheduleReconnect]);

  return (
    <ReconnectingContext.Provider value={reconnecting}>
      <ConnectedContext.Provider value={connected}>
        <SdkProvider connectionBuilder={builder}>{children}</SdkProvider>
      </ConnectedContext.Provider>
    </ReconnectingContext.Provider>
  );
}

export { useSpacetimeDB };
export type { DbConnection };
